<template>
  <v-card id="settlement-activity-breakdown" :class="contractModeLabel">
    <v-card-title :class="contractClass">
      <Icon
      :icon="headerIcon.icon"
      :tooltipText="$t(headerIcon.tooltip)"
      iconColor="white"
      :small="false"
      margin="mr-2"
      />
      <span>
        {{$t('activitiesForPayeeTitle', { payeeName: counterpartyName })}}
      </span>
      <v-spacer></v-spacer>
      <BaseDialogActions hideRefresh/>
    </v-card-title>
    <v-card-text>
      <v-container fluid>
        <v-row>
          <v-col cols="12" md="6">
            <v-card outlined class="fill-height">
              <v-card-title>
                <span class="subtitle-1">{{$t('contractInformation')}}</span>
              </v-card-title>
              <v-card-text>
                <v-row dense>
                  <v-col cols="12" md="6" v-for="(ci, i) in contractIcons" :key="i">
                    <Icon
                      :icon="ci.icon"
                      :tooltipText="$t(ci.tooltip)"
                      iconColor="grey"
                    />
                    <span class="ml-2">{{ci.value(contractInfo)}}</span>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="6">
            <v-card outlined class="fill-height">
              <v-card-title>
                <span class="subtitle-1">{{$t('totalsForEntity', { entity: counterpartyName })}}</span>
              </v-card-title>
              <v-card-text>
                <v-row dense>
                  <v-col cols="12" md="6" v-for="(tt, i) in totalsTable" :key="i">
                    <v-row>
                      <v-col cols="6" xl="4">
                        <span>{{$t(tt.label)}}: </span>
                      </v-col>
                      <v-col cols="auto">
                        <span>{{tt.value ?? $t('notAvailable')}}</span>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" class="py-0 my-n2">
            <v-switch
            v-model="ticketSummaries"
            color="primary"
            id="show-ticket-summaries"
            :label="$t('showTicketSummaries')"
          ></v-switch>
          </v-col>
          <v-col cols="12">
            <v-expansion-panels popout v-model="openPanels" multiple>
              <v-expansion-panel v-for="(activity) in paymentRollup" :key="activity.activityId">
                <v-expansion-panel-header :style="{ height: 'auto', paddingTop: '8px', paddingBottom: '8px' }">
                  <v-row dense align="center">
                    <v-col cols="12" :md="isFullscreen ? 9 : 8">
                      <Icon v-if="showPenaltyIcon(activity)" icon="weight-alert" :small="false" :tooltipText="$t('penalty')"/>
                      <Icon
                        v-if="activityHasNegativePayment(activity)"
                        icon="mdi-alert"
                        iconColor="warning"
                        tooltipColor="warning"
                        :small="false"
                        :tooltipText="$t('activityHasNegativePayment')"
                        elementId="sbc-activity-negative-payment"/>
                      {{activity.activityName}}
                      <Icon
                        v-if="activityScaleLinesFor(activity.activityId).length > 0"
                        icon="mdi-ruler-square"
                        iconColor="success"
                        :small="false"
                        :tooltipText="$t('scaleSummary')"
                        @icon-clicked.stop="openScaleSummaryDialog(activity)"
                      />
                    </v-col>
                    <v-col cols="12" :md="isFullscreen ? 3 : 4">
                      <table style="border-collapse: separate; table-layout: fixed; width: 100%; min-width: 180px" class="body-2">
                        <tr>
                          <td style="width: 100px;">{{$t('payout')}}: </td>
                          <td class="text-right">
                            {{formatMoney(activity.payout ?? activity.amount)}}
                            {{getRecoveredText(activity)}}
                          </td>
                        </tr>
                        <tr>
                          <td>{{$t('avgRate')}}: </td>
                          <td class="text-right">
                            <Icon
                            v-if="splitByActivity.get(activity.activityName)?.length > 0"
                            iconColor="primary"
                            icon="mdi-percent-circle-outline"
                            :small="false"
                            :tooltipText="$t('reflectsOwnershipPercentage', { x: ownershipPercentageValue(splitByActivity.get(activity.activityName)) })"/>
                            {{ getRateText(activity.averageRate, activity.payBy, activity.payOn, activity.modifier, activity.grossVolume, activity.netVolume, activity.payout ?? activity.amount, activity.isDeduction) }}
                          </td>
                        </tr>
                        <tr>
                          <td>{{$t('unit')}}: </td>
                          <td class="text-right">
                            {{getUnitText(activity)}}
                          </td>
                        </tr>
                      </table>
                    </v-col>
                  </v-row>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-data-table
                  :items="activity.productSummaries"
                  :headers="productHeaders(activity)"
                  dense
                  :hide-default-footer="activity.productSummaries.length < 10"
                  class="mt-2"
                  >
                    <template #item.payOnUnit="{item}">
                      <span>{{gePayOnUnitForProductSummary(item, activity)}}</span>
                    </template>
                    <template #item.averageRate="{item}">
                      {{ getRateText(item.rate, activity.payBy, activity.payOn, activity.modifier, item.grossVolume, item.netVolume, item.payout, activity.isDeduction) }}
                      <span v-if="splitByActivity.get(activity.activityName)?.length > 0">
                       x {{ ownershipPercentageValue(splitByActivity.get(activity.activityName)) }}
                      </span>
                    </template>
                    <template #item.penaltyAmount="{item}">
                      <span>{{formatMoney((item.rate * item.netTons) - item.payout)}}</span>
                    </template>
                    <template #item.payout="{item}">
                      <span>{{formatMoney(item.payout)}}</span>
                    </template>
                    <template #item.recovered="{item}">
                      <span>{{formatMoney(item.grossPayout - item.payout)}}</span>
                    </template>
                    <template #item.grossTons="{item}">
                      <span>{{item.grossTons}}t</span>
                    </template>
                    <template #item.netTons="{item}">
                      <span>{{item.netTons}}t</span>
                    </template>
                    <template #item.grossVolume="{item}">
                      <span>{{mbfStringFromBf(item.grossVolume)}}</span>
                    </template>
                    <template #item.netVolume="{item}">
                      <span>{{mbfStringFromBf(item.netVolume)}}</span>
                    </template>
                  </v-data-table>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-col>
          <v-col cols="12" v-if="ticketSummaries">
            <v-card outlined>
              <v-card-title>
                <span class="subtitle-1">{{$t('ticketSummaries')}}</span>
              </v-card-title>
              <v-card-text>
                <v-data-table
                  :items="loadSummariesForTable"
                  :headers="loadSummaryHeaders"
                  :hide-default-footer="loadSummariesForTable.length < 10"
                  dense>
              <template #item.ticketNumber="{item}">
                <a
                v-if="item.ticketId"
                class="primary--text text-decoration-underline"
                @click.stop="openTicketDetailDialog(item)">{{item.ticketNumber}}</a>
                <span v-else>{{item.ticketNumber}}</span>
              </template>
              <template #item.grossVolume="{item}">
                {{mbfStringFromBf(item.grossVolume)}}
              </template>
              <template #item.netVolume="{item}">
                {{mbfStringFromBf(item.netVolume)}}
              </template>
              <template #item.actions="{item}">
                <Icon
                icon="mdi-briefcase"
                :tooltipText="$t('unpostTicket')"
                iconColor="error"
                @icon-clicked="showUnpostDialog(item)"
                elementId="unpost-ticket"/>
              </template>
              </v-data-table>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>
    <Dialog :stateId="unpostDialogId" @close="resetUnpostDialog">
      <ConfirmDialog
        v-if="focusedTicketForUnpost"
        :title="$t('unpostTicket')"
        :body="$t('unpostTicketBodySBP')"
        :confirmText="$t('unpostTicket')"
        @cancel="closeUnpostDialog"
        @confirm="unpostFocusedTicket"
      />
    </Dialog>
    <Dialog stateId="contract-scale-summary-dialog" maxWidth="80vw" @dialog-closing="scaleSummaryDialog = false">
      <ContractScaleSummaryDialog
        v-if="scaleSummaryDialog"
        :lines="focusedScaleLines"
        :title="focusedScaleTitle"
      />
    </Dialog>
    <Dialog :stateId="ticketDetailDialogId" maxWidth="80vw" @close="resetTicketDetailDialog">
      <TicketDetail
        v-if="ticketDetailDialog"
        :ticketId="focusedTicketId"
      />
    </Dialog>
  </v-card>
</template>

<script>
import { settlementProductHeaders, contractTicketHeaders } from '@/headers/Cycles.js'
import { PayOn, PayBy, ActivityModifier, ContractType } from '@/utils/Enumerations.js'
import { NumericConstants } from '@/utils/constants'
import { contractIconsFor, settlementHeaderIconFor } from '@/components/settlements/accounts-payable-module/ContractIcons'
import { chunk } from 'lodash'
import { mapGetters, mapActions } from 'vuex'
import { formatMoney, numberWithCommas, tonStringFromPounds, formatPercentage, mbfStringFromBf } from '@/utils/NumericMutations'
import { colorClassForContractType, uniqueDialogId } from '@/utils/componentHelpers.js'
import { TemplateSpecialization, ContractMode } from '@/utils/Enumerations'
import { activityHasNegativePayment } from '@/components/settlements/NegativePaymentHelpers.js'
import { settlementColumnContextFor } from '@/components/settlements/SettlementKeys.js'
export default {
  name: 'SettlementActivityBreakdown',

  props: {
    propPayee: {
      type: Object,
      required: true
    },
    propContractInfo: {
      type: Object,
      required: true
    },
    isReceivable: {
      type: Boolean,
      required: false
    },
    isJournalEntry: {
      type: Boolean,
      required: false
    }
  },

  components: {
    ConfirmDialog: () => import('@/components/helper/ConfirmDialog.vue'),
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    ContractScaleSummaryDialog: () => import('@/components/settlements/accounts-payable-module/settlements-by-payee/ContractScaleSummaryDialog.vue'),
    TicketDetail: () => import('@/components/ticket/ticket-details/TicketDetails.vue'),
    Dialog: () => import('@/components/Dialog.vue')
  },

  data: () => ({
    ticketSummaries: false,
    activityModifier: ActivityModifier,
    unpostDialogId: uniqueDialogId('unpost-ticket'),
    focusedTicketForUnpost: undefined,
    loadSummariesForTable: undefined,
    splitByActivity: new Map(),
    openPanels: [],
    scaleSummaryDialog: false,
    focusedScaleLines: [],
    focusedScaleTitle: '',
    ticketDetailDialogId: uniqueDialogId('ticket-detail'),
    ticketDetailDialog: false,
    focusedTicketId: undefined
  }),

  watch: {
    propContractInfo: {
      handler (_) {
        this.calculateLoadSummaries()
      },
      deep: true
    }
  },

  created () {
    this.calculateLoadSummaries()
    this.initSplitByActivityMap()
    this.openPanels = this.paymentRollup.length === 1 ? [0] : []
  },

  computed: {
    ...mapGetters('dialog', ['topDialogState']),
    ...mapGetters('settlements', ['currentModuleKey', 'currentSubModuleKey', 'dateConfiguration']),
    isTransfer () { return this.propContractInfo.type === ContractType.Transfer.value },
    isByproduct () { return this.propContractInfo.type === ContractType.ByproductSale.value || this.propContractInfo.type === ContractType.ByproductPurchase.value || this.propContractInfo.type === ContractType.ByproductHauling.value },

    contractClass () {
      return colorClassForContractType(this.propContractInfo.type)
    },

    contractModeLabel () {
      return this.isByproduct ? ContractMode.Byproducts.label : ContractMode.Logs.label
    },

    savedColumnsContext () {
      return settlementColumnContextFor(this.currentModuleKey, this.currentSubModuleKey)
    },

    loadSummaryHeaders () {
      const includeVolume = !this.isByproduct && this.loadSummaries.some(t => t.netVolume > 0 || t.grossVolume > 0)
      const headers = contractTicketHeaders(this.savedColumnsContext).getColumns()
      return headers
        .filter(h => (!this.isTransfer && !this.isReceivable) || h.value !== 'grossAmount')
        .filter(h => !this.isJournalEntry || h.value !== 'grossAmount')
        .filter(h => includeVolume || (h.value !== 'grossVolume' && h.value !== 'netVolume'))
    },

    isFullscreen () {
      return this.topDialogState?.fullscreen ?? false
    },

    payeeHasPayout () {
      return this.propPayee.payout !== undefined && this.propPayee.payout !== null
    },

    loadSummaries () {
      return (this.payeeHasPayout) ? this.propPayee.loadSummaries : this.propContractInfo.loadSummaries
    },

    totals () {
      return (this.isReceivable)
        ? {
          payout: this.propContractInfo.amount,
          loads: this.propContractInfo.loads,
          grossTons: this.propContractInfo.grossTons,
          netTons: this.propContractInfo.netTons,
          recovered: 0
        } : {
          payout: this.payee.payout,
          loads: this.contractInfo.loads,
          grossTons: this.contractInfo.grossTons,
          netTons: this.contractInfo.netTons,
          recovered: (typeof this.payee.grossPayout === 'number') ? this.payee.grossPayout - this.payee.payout : 0
        }
    },

    grossTons () {
      return (this.isReceivable) ? this.propPayee.grossTons : this.contractInfo.grossTons
    },

    paymentRollup () {
      return (this.payeeHasPayout) ? this.propPayee.paymentRollup : this.propContractInfo.paymentRollup
    },

    contractInfo () {
      if (this.propContractInfo.loads) {
        return this.propContractInfo
      } else {
        const contractInfo = { ...this.propContractInfo }
        contractInfo.loads = this.propContractInfo.loadSummaries.length
        return contractInfo
      }
    },

    payee () {
      if ((this.propPayee.payout !== undefined && this.propPayee.payout !== null) || this.isReceivable) {
        return this.propPayee
      } else {
        const payee = { ...this.propContractInfo }
        payee.payeeName = this.propPayee.payeeName
        payee.payout = this.propContractInfo.amount
        payee.grossPayout = this.propContractInfo.grossAmount
        return payee
      }
    },

    counterpartyName () {
      return this.propPayee?.counterpartyName ?? this.propPayee?.payeeName
    },

    contractIcons () {
      return this.contractIconsFor(this.contractInfo.type, 'contract', (this.isReceivable) ? {
        destination: {
          value: c => c.destinationAccountName
        },
        fromAccount: {
          value: c => c.fromAccountName
        }
      } : {})
    },

    headerIcon () {
      return settlementHeaderIconFor(this.contractInfo.type)
    },

    volumeInfo () {
      const payee = this.isJournalEntry ? undefined : this.propPayee
      return {
        grossVolume: payee?.grossVolume ?? this.propContractInfo?.grossVolume,
        netVolume: payee?.netVolume ?? this.propContractInfo?.netVolume
      }
    },

    totalsTable () {
      const totals = [
        {
          label: 'payout',
          value: (this.totals.recovered > 0)
            ? `${formatMoney(this.totals.payout)} (${this.$t('xRecovered', { x: formatMoney(this.totals.recovered) })})`
            : `${formatMoney(this.totals.payout)}`
        },
        {
          label: 'loads',
          value: this.totals.loads
        },
        {
          label: 'grossTons',
          value: this.totals.grossTons
        },
        {
          label: 'netTons',
          value: this.totals.netTons
        }
      ]

      if (this.volumeInfo.grossVolume > 0 || this.volumeInfo.netVolume > 0) {
        totals.push(
          {
            label: 'grossVolume',
            value: this.$t('xThousandBoardFeet', { x: numberWithCommas(this.volumeInfo.grossVolume / NumericConstants.MbfDivisor, 3) })
          },
          {
            label: 'netVolume',
            value: this.$t('xThousandBoardFeet', { x: numberWithCommas(this.volumeInfo.netVolume / NumericConstants.MbfDivisor, 3) })
          })
      }

      return totals
    },

    isExportBatch () {
      return this.dateConfiguration?.exportBatch?.exportBatchId !== undefined
    }
  },

  methods: {
    ...mapActions('posting', ['unpostTickets']),
    ...mapActions('dialog', ['openDialog', 'openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    formatMoney,
    numberWithCommas,
    activityHasNegativePayment,
    tonStringFromPounds,
    mbfStringFromBf,
    chunk,
    contractIconsFor,

    getUnitText ({
      payOn,
      payBy,
      modifier,
      grossTons,
      grossMetricTons,
      defectTons,
      mismanufactureDefectTons,
      natureDefectTons,
      netTons,
      netMetricTons,
      grossVolume,
      netVolume
    }) {
      if (payBy === PayBy.Load.value) return this.$t('payByLoad')
      if (modifier === ActivityModifier.DistanceTraveled.value) { return this.$t('distanceInMiles', { distance: this.contractInfo.distance }) }
      if (payBy === PayBy.Volume.value) {
        return payOn === PayOn.Gross.value
          ? this.$t('xGrossThousandBoardFeet', { x: mbfStringFromBf(grossVolume) })
          : this.$t('xNetThousandBoardFeet', { x: mbfStringFromBf(netVolume) })
      }

      const isMetric = payBy === PayBy.MetricWeight.value

      let weight = 0
      switch (payOn) {
        case PayOn.Gross.value:
          weight = isMetric ? grossMetricTons : grossTons
          break
        case PayOn.Net.value:
          weight = isMetric ? netMetricTons : netTons
          break
        // Metric weight not suppored for defect types
        case PayOn.Defect.value: weight = defectTons; break
        case PayOn.NatureDefect.value: weight = natureDefectTons; break
        case PayOn.MismanufacturedDefect.value: weight = mismanufactureDefectTons; break
        default: weight = netTons
      }

      const weightText = isMetric
        ? this.$t('metricTonsTotal', { totalWeight: weight })
        : this.$t('shortTonsTotal', { totalWeight: weight })

      return `${PayOn.fromInt(payOn)}: ${weightText}`
    },

    getRateText (rate, payBy, payOn, modifier, grossVolume, netVolume, amount, isDeduction) {
      const formattedRate = formatMoney(isDeduction ? Math.abs(rate) : rate)

      if (modifier === ActivityModifier.DistanceTraveled.value) return `${formattedRate}/m`

      if (payBy === PayBy.Load.value) return formattedRate

      if (payBy === PayBy.Volume.value) {
        const mbf = payOn === PayOn.Gross.value
          ? grossVolume / NumericConstants.MbfDivisor
          : netVolume / NumericConstants.MbfDivisor

        const payout = isDeduction ? Math.abs(amount) : amount

        return this.$t('xThousandBoardFeet', { x: formatMoney(mbf > 0 ? payout / mbf : 0) })
      }

      const denominator = payBy === PayBy.MetricWeight.value ? 'mt' : 't'
      return `${formattedRate}/${denominator}`
    },

    gePayOnUnitForProductSummary (productSummary, activity) {
      if (activity.payBy === PayBy.Load.value) return this.$t('payByLoad')

      if (activity.payBy === PayBy.Volume.value) {
        return activity.payOn === PayOn.Gross.value
          ? this.$t('xGrossThousandBoardFeet', { x: numberWithCommas(productSummary.grossVolume / NumericConstants.MbfDivisor, 3) })
          : this.$t('xNetThousandBoardFeet', { x: numberWithCommas(productSummary.netVolume / NumericConstants.MbfDivisor, 3) })
      }

      let payOnUnit
      const isMetric = activity.payBy === PayBy.MetricWeight.value

      switch (activity.payOn) {
        case PayOn.Gross.value:
          payOnUnit = isMetric ? productSummary.grossMetricTons : productSummary.grossTons
          break
        case PayOn.Net.value:
          payOnUnit = isMetric ? productSummary.netMetricTons : productSummary.netTons
          break
        // Metric Tons not suppored for defect types
        case PayOn.Defect.value: payOnUnit = productSummary.defectTons; break
        case PayOn.NatureDefect.value: payOnUnit = productSummary.natureDefectTons; break
        case PayOn.MismanufacturedDefect.value: payOnUnit = productSummary.mismanufactureDefectTons; break
        default: payOnUnit = productSummary.netTons
      }

      return isMetric
        ? this.$t('metricTonsTotal', { totalWeight: payOnUnit })
        : this.$t('shortTonsTotal', { totalWeight: payOnUnit })
    },

    getNetTons ({ inWeight, outWeight, defectWeight }) {
      return tonStringFromPounds(inWeight - outWeight - defectWeight)
    },

    getGrossTons ({ inWeight, outWeight }) {
      return tonStringFromPounds(inWeight - outWeight)
    },

    getRecoveredText ({ payout, grossPayout }) {
      if (payout === grossPayout) {
        return ''
      } else {
        return `(${formatMoney(grossPayout - payout)} Recovered)`
      }
    },

    showUnpostDialog (ticket) {
      if (this.isExportBatch) {
        this.setSnackError(this.$t('ticketsCannotBeUnpostedInExportBatch'))
        return
      }

      this.focusedTicketForUnpost = ticket
      this.openOrUpdateDialog({ id: this.unpostDialogId, width: '400px' })
    },

    closeUnpostDialog () {
      this.closeDialogsAtOrAbove(this.unpostDialogId)
    },

    resetUnpostDialog () {
      this.focusedTicketForUnpost = undefined
    },

    async unpostFocusedTicket () {
      const request = {
        ticketIds: [this.focusedTicketForUnpost.ticketId],
        postBatchId: this.focusedTicketForUnpost.postBatchId
      }

      await this.unpostTickets(request)

      this.$emit('refresh-settlement')
      this.closeUnpostDialog()
    },

    calculateLoadSummaries () {
      this.loadSummariesForTable = JSON.parse(JSON.stringify(this.loadSummaries))
      this.loadSummariesForTable.forEach(item => {
        item.gross = this.getGrossTons(item)
        item.netWeight = this.getNetTons(item)
        item.grossAmount = this.formatMoney(item.grossAmount - item.amount)
        item.amount = this.formatMoney(item.amount)
        item.defectWeight = this.tonStringFromPounds(item.defectWeight)
      })
    },

    initSplitByActivityMap () {
      const loadSummaryRoot = this.payeeHasPayout ? this.propPayee.loadSummaries : this.propPayee.contracts.flatMap(c => c.loadSummaries)
      if (loadSummaryRoot.length === 0 || !loadSummaryRoot[0].paymentDetails) return
      loadSummaryRoot.forEach(ls => ls.paymentDetails.forEach(pd => {
        const key = pd.activity
        const splits = this.splitByActivity.get(key) ?? []
        if (pd.computedAtSplit !== null && pd.computedAtSplit !== 100) splits.push(pd.computedAtSplit)
        this.splitByActivity.set(key, splits)
      }))
    },

    ownershipPercentageValue (val) {
      return val.length === 1 ? formatPercentage(val[0]) : this.$t('multipleSplits')
    },

    showPenaltyIcon (activity) {
      return activity.activityTemplateSpecialization === TemplateSpecialization.Penalty.value
    },

    activityScaleLinesFor (activityId) {
      return (this.propPayee?.scaleSummary ?? this.propContractInfo?.scaleSummary ?? []).filter(l => l.activityId === activityId)
    },

    openScaleSummaryDialog (activity) {
      this.focusedScaleLines = this.activityScaleLinesFor(activity.activityId)
      this.focusedScaleTitle = `${this.$t('scaleSummary')}: ${activity.activityName}`
      this.scaleSummaryDialog = true
      this.openDialog({ id: 'contract-scale-summary-dialog', allowFullscreen: true })
    },

    openTicketDetailDialog (ticket) {
      this.focusedTicketId = ticket.ticketId
      this.ticketDetailDialog = true
      this.openDialog({ id: this.ticketDetailDialogId, allowFullscreen: true })
    },

    resetTicketDetailDialog () {
      this.ticketDetailDialog = false
      this.focusedTicketId = undefined
    },

    productHeaders (activity) {
      const includeVolume = !this.isByproduct && activity.productSummaries.some(p => p.netVolume > 0 || p.grossVolume > 0)
      return settlementProductHeaders(this.savedColumnsContext).getColumns().filter(h =>
        ((!this.isTransfer &&
        !this.isReceivable &&
        activity.activityTemplateSpecialization !== TemplateSpecialization.Penalty.value) || h.value !== 'recovered') &&
        (this.isReceivable || h.value !== 'penaltyAmount')
      ).filter(h => !this.isJournalEntry || h.value !== 'recovered')
        .filter(h => includeVolume || (h.value !== 'grossVolume' && h.value !== 'netVolume'))
    }
  }
}
</script>
